import localforage from 'localforage';

// Configure IndexedDB stores
const healthRecords = localforage.createInstance({
  name: 'HealthPortal',
  storeName: 'health_records'
});

const conversations = localforage.createInstance({
  name: 'HealthPortal',
  storeName: 'conversations'
});

const userAuth = localforage.createInstance({
  name: 'HealthPortal',
  storeName: 'user_auth'
});

// User preferences store (for personalization - stored locally)
const userPreferences = localforage.createInstance({
  name: 'HealthPortal',
  storeName: 'user_preferences'
});

// Mood check-ins store (daily mood tracking)
const moodCheckins = localforage.createInstance({
  name: 'HealthPortal',
  storeName: 'mood_checkins'
});

// Predefined health record categories
export const HEALTH_CATEGORIES = [
  { id: 'lab_results', name: 'Lab Results', icon: '🧪', color: 'blue' },
  { id: 'prescriptions', name: 'Prescriptions', icon: '💊', color: 'green' },
  { id: 'imaging', name: 'Imaging (X-Ray, MRI, CT)', icon: '🩻', color: 'purple' },
  { id: 'vaccinations', name: 'Vaccinations', icon: '💉', color: 'teal' },
  { id: 'doctor_notes', name: 'Doctor Notes', icon: '📝', color: 'yellow' },
  { id: 'insurance', name: 'Insurance Documents', icon: '📋', color: 'slate' },
  { id: 'dental', name: 'Dental Records', icon: '🦷', color: 'cyan' },
  { id: 'vision', name: 'Vision/Eye Exams', icon: '👁️', color: 'indigo' },
  { id: 'mental_health', name: 'Mental Health', icon: '🧠', color: 'pink' },
  { id: 'allergies', name: 'Allergies', icon: '🤧', color: 'red' },
  { id: 'surgeries', name: 'Surgical Records', icon: '🏥', color: 'orange' },
  { id: 'physical_exam', name: 'Physical Exam', icon: '🩺', color: 'emerald' },
  { id: 'other', name: 'Other', icon: '📁', color: 'gray' }
];

// Helper to get category by ID
export const getCategoryById = (id) => {
  return HEALTH_CATEGORIES.find(cat => cat.id === id) || HEALTH_CATEGORIES.find(cat => cat.id === 'other');
};

// Helper to get category color class
export const getCategoryColorClass = (categoryId, type = 'bg') => {
  const category = getCategoryById(categoryId);
  const colorMap = {
    blue: { bg: 'bg-blue-600', text: 'text-blue-400', border: 'border-blue-500' },
    green: { bg: 'bg-green-600', text: 'text-green-400', border: 'border-green-500' },
    purple: { bg: 'bg-purple-600', text: 'text-purple-400', border: 'border-purple-500' },
    teal: { bg: 'bg-teal-600', text: 'text-teal-400', border: 'border-teal-500' },
    yellow: { bg: 'bg-yellow-600', text: 'text-yellow-400', border: 'border-yellow-500' },
    slate: { bg: 'bg-slate-600', text: 'text-slate-400', border: 'border-slate-500' },
    cyan: { bg: 'bg-cyan-600', text: 'text-cyan-400', border: 'border-cyan-500' },
    indigo: { bg: 'bg-indigo-600', text: 'text-indigo-400', border: 'border-indigo-500' },
    pink: { bg: 'bg-pink-600', text: 'text-pink-400', border: 'border-pink-500' },
    red: { bg: 'bg-red-600', text: 'text-red-400', border: 'border-red-500' },
    orange: { bg: 'bg-orange-600', text: 'text-orange-400', border: 'border-orange-500' },
    emerald: { bg: 'bg-emerald-600', text: 'text-emerald-400', border: 'border-emerald-500' },
    gray: { bg: 'bg-gray-600', text: 'text-gray-400', border: 'border-gray-500' }
  };
  return colorMap[category?.color || 'gray'][type];
};

class HealthStorageService {
  // ========== HEALTH RECORDS ==========
  
  async saveHealthRecord(record) {
    const id = record.id || `record_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const recordWithId = {
      ...record,
      id,
      category: record.category || 'other',
      createdAt: record.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await healthRecords.setItem(id, recordWithId);
    return recordWithId;
  }
  
  async getHealthRecord(id) {
    return await healthRecords.getItem(id);
  }
  
  async getAllHealthRecords() {
    const records = [];
    await healthRecords.iterate((value) => {
      records.push(value);
    });
    return records.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  
  async getRecordsByCategory(categoryId) {
    const allRecords = await this.getAllHealthRecords();
    return allRecords.filter(record => record.category === categoryId);
  }
  
  async updateHealthRecord(id, updates) {
    const existing = await healthRecords.getItem(id);
    if (!existing) {
      throw new Error('Record not found');
    }
    const updated = {
      ...existing,
      ...updates,
      id, // Preserve original ID
      createdAt: existing.createdAt, // Preserve original creation date
      updatedAt: new Date().toISOString()
    };
    await healthRecords.setItem(id, updated);
    return updated;
  }
  
  async deleteHealthRecord(id) {
    await healthRecords.removeItem(id);
  }
  
  // ========== TIMELINE INSIGHTS (Feb 4, 2026) ==========
  
  /**
   * Save AI-generated insights to a health record
   * NOTE: Only keeps the LATEST insight per record (clears previous insights)
   * 
   * @param {string} recordId - The ID of the primary record to save insights to
   * @param {object} insight - The insight data
   * @param {string} insight.text - The AI-generated insight text
   * @param {string[]} [insight.comparedRecordIds] - IDs of other records used in comparison
   * @param {string} [insight.type] - Type of insight ('single' or 'comparison')
   * @param {object} [insight.metrics] - Any extracted metrics from the insight
   */
  async saveInsightToRecord(recordId, insight) {
    const existing = await healthRecords.getItem(recordId);
    if (!existing) {
      throw new Error('Record not found');
    }
    
    // Build insight entry
    const insightEntry = {
      id: `insight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: insight.text,
      type: insight.type || 'single',
      comparedRecordIds: insight.comparedRecordIds || [],
      metrics: insight.metrics || null,
      generatedAt: new Date().toISOString(),
      source: 'timeline'
    };
    
    // ONLY keep the latest insight - replace any existing insights (Feb 4, 2026 - user feedback)
    const updatedInsights = [insightEntry];
    
    // Update the record
    const updated = {
      ...existing,
      timelineInsights: updatedInsights,
      lastInsightAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await healthRecords.setItem(recordId, updated);
    return { record: updated, insight: insightEntry };
  }
  
  /**
   * Get all insights for a specific record
   */
  async getRecordInsights(recordId) {
    const record = await healthRecords.getItem(recordId);
    return record?.timelineInsights || [];
  }
  
  /**
   * Delete a specific insight from a record
   */
  async deleteInsightFromRecord(recordId, insightId) {
    const existing = await healthRecords.getItem(recordId);
    if (!existing) {
      throw new Error('Record not found');
    }
    
    const updatedInsights = (existing.timelineInsights || []).filter(i => i.id !== insightId);
    
    const updated = {
      ...existing,
      timelineInsights: updatedInsights,
      updatedAt: new Date().toISOString()
    };
    
    await healthRecords.setItem(recordId, updated);
    return updated;
  }
  
  async searchRecords(query) {
    const allRecords = await this.getAllHealthRecords();
    const queryLower = query.toLowerCase();
    return allRecords.filter(record => {
      const searchableText = [
        record.title,
        record.category,
        record.ocrText,
        record.notes
      ].join(' ').toLowerCase();
      return searchableText.includes(queryLower);
    });
  }
  
  async getRecordStats() {
    const allRecords = await this.getAllHealthRecords();
    const categoryStats = {};
    
    for (const category of HEALTH_CATEGORIES) {
      categoryStats[category.id] = {
        ...category,
        count: allRecords.filter(r => r.category === category.id).length
      };
    }
    
    // Count uncategorized
    const uncategorized = allRecords.filter(r => !r.category || r.category === 'Uncategorized').length;
    if (uncategorized > 0) {
      categoryStats['other'].count += uncategorized;
    }
    
    return {
      total: allRecords.length,
      byCategory: categoryStats,
      oldestRecord: allRecords.length > 0 ? allRecords[allRecords.length - 1].createdAt : null,
      newestRecord: allRecords.length > 0 ? allRecords[0].createdAt : null
    };
  }
  
  // ========== CONVERSATIONS ==========
  
  async saveConversation(conversation) {
    const id = conversation.id || `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const convWithId = {
      ...conversation,
      id,
      createdAt: conversation.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    await conversations.setItem(id, convWithId);
    return convWithId;
  }
  
  async getConversation(id) {
    return await conversations.getItem(id);
  }
  
  async getAllConversations() {
    const convs = [];
    await conversations.iterate((value) => {
      convs.push(value);
    });
    return convs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }
  
  async deleteConversation(id) {
    await conversations.removeItem(id);
  }
  
  // ========== USER AUTH (LOCAL) ==========
  
  async saveLocalUser(userData) {
    const encryptedData = {
      ...userData,
      createdAt: userData.createdAt || new Date().toISOString()
    };
    await userAuth.setItem('current_user', encryptedData);
    return encryptedData;
  }
  
  async getLocalUser() {
    return await userAuth.getItem('current_user');
  }
  
  async deleteLocalUser() {
    await userAuth.removeItem('current_user');
  }
  
  // ========== STORAGE STATS ==========
  
  async getStorageStats() {
    const recordCount = (await this.getAllHealthRecords()).length;
    const convCount = (await this.getAllConversations()).length;
    
    // Estimate storage size (rough calculation)
    let totalSize = 0;
    await healthRecords.iterate((value) => {
      totalSize += JSON.stringify(value).length;
    });
    await conversations.iterate((value) => {
      totalSize += JSON.stringify(value).length;
    });
    
    return {
      recordCount,
      conversationCount: convCount,
      estimatedSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    };
  }
  
  // ========== EXPORT/IMPORT ==========
  
  async exportAllData() {
    const records = await this.getAllHealthRecords();
    const convs = await this.getAllConversations();
    const moods = await this.getAllMoodCheckins();
    const prefs = await this.getUserPreferences();
    const user = await this.getLocalUser();
    
    return {
      exportDate: new Date().toISOString(),
      version: '1.1', // Backup format version
      healthRecords: records,
      conversations: convs,
      moodCheckins: moods,
      preferences: prefs,
      user: user
    };
  }
  
  async importData(data) {
    let imported = { records: 0, conversations: 0, moods: 0 };
    
    // Import health records
    if (data.healthRecords) {
      for (const record of data.healthRecords) {
        await this.saveHealthRecord(record);
        imported.records++;
      }
    }
    
    // Import conversations
    if (data.conversations) {
      for (const conv of data.conversations) {
        await this.saveConversation(conv);
        imported.conversations++;
      }
    }
    
    // Import mood check-ins (v1.1+)
    if (data.moodCheckins) {
      for (const mood of data.moodCheckins) {
        await this.saveMoodCheckin(mood);
        imported.moods++;
      }
    }
    
    // Import preferences (v1.1+)
    if (data.preferences) {
      await this.saveUserPreferences(data.preferences);
    }
    
    // Import user
    if (data.user) {
      await this.saveLocalUser(data.user);
    }
    
    return imported;
  }
  
  // ========== PERSISTENT STORAGE ==========
  
  /**
   * Request persistent storage from the browser
   * This helps prevent auto-eviction of IndexedDB data
   * NOTE: Does NOT prevent manual "Clear cache" by user
   */
  async requestPersistentStorage() {
    if (navigator.storage && navigator.storage.persist) {
      try {
        const isPersisted = await navigator.storage.persist();
        console.log(`[Storage] Persistent storage granted: ${isPersisted}`);
        return isPersisted;
      } catch (error) {
        console.error('[Storage] Failed to request persistent storage:', error);
        return false;
      }
    }
    console.log('[Storage] Persistent Storage API not supported');
    return false;
  }
  
  /**
   * Check if persistent storage is already granted
   */
  async isPersisted() {
    if (navigator.storage && navigator.storage.persisted) {
      return await navigator.storage.persisted();
    }
    return false;
  }
  
  /**
   * Get storage quota and usage information
   */
  async getStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
          percentUsed: estimate.quota ? ((estimate.usage / estimate.quota) * 100).toFixed(2) : 0
        };
      } catch (error) {
        console.error('[Storage] Failed to get storage estimate:', error);
        return null;
      }
    }
    return null;
  }
  
  /**
   * Get backup metadata (last backup date, record counts)
   */
  async getBackupMetadata() {
    const prefs = await this.getUserPreferences();
    const records = await this.getAllHealthRecords();
    const conversations = await this.getAllConversations();
    const moods = await this.getAllMoodCheckins();
    
    return {
      lastBackupDate: prefs.lastBackupDate || null,
      recordCount: records.length,
      conversationCount: conversations.length,
      moodCount: moods.length,
      totalItems: records.length + conversations.length + moods.length
    };
  }
  
  /**
   * Update last backup date
   */
  async setLastBackupDate() {
    await this.saveUserPreferences({ 
      lastBackupDate: new Date().toISOString() 
    });
  }
  
  async clearAllData() {
    await healthRecords.clear();
    await conversations.clear();
    await userAuth.clear();
    await userPreferences.clear();
    await moodCheckins.clear();
  }
  
  // ========== USER PREFERENCES (Personalization) ==========
  
  async getUserPreferences() {
    const prefs = await userPreferences.getItem('preferences');
    if (!prefs) {
      return {
        preferredName: '',
        firstVisitComplete: false,
        createdAt: null,
        lastVisit: null,
        visitCount: 0,
        userAvatar: '👤',  // Default user avatar
        drBenAvatar: '👨‍⚕️' // Default Dr. Ben avatar (simple emoji as requested)
      };
    }
    
    // Self-Repair: Avatar Key Migration — auto-detect and migrate `avatar` → `userAvatar`
    if (prefs.avatar && !prefs.userAvatar) {
      console.log('[Self-Repair] Migrating avatar key: avatar →  userAvatar:', prefs.avatar);
      prefs.userAvatar = prefs.avatar;
      delete prefs.avatar;
      await userPreferences.setItem('preferences', prefs);
    }
    
    return prefs;
  }
  
  // Alias used by HealthContext (merges into existing prefs)
  async updateUserPreferences(prefs) {
    return this.saveUserPreferences(prefs);
  }

  async saveUserPreferences(prefs) {
    const existing = await this.getUserPreferences();
    const updated = {
      ...existing,
      ...prefs,
      lastVisit: new Date().toISOString()
    };
    if (!updated.createdAt) {
      updated.createdAt = new Date().toISOString();
    }
    await userPreferences.setItem('preferences', updated);
    return updated;
  }
  
  async setPreferredName(name) {
    const prefs = await this.getUserPreferences();
    return await this.saveUserPreferences({
      ...prefs,
      preferredName: name
    });
  }
  
  async getPreferredName() {
    const prefs = await this.getUserPreferences();
    return prefs.preferredName || '';
  }
  
  async markFirstVisitComplete() {
    const prefs = await this.getUserPreferences();
    return await this.saveUserPreferences({
      ...prefs,
      firstVisitComplete: true,
      visitCount: (prefs.visitCount || 0) + 1
    });
  }
  
  async incrementVisitCount() {
    const prefs = await this.getUserPreferences();
    return await this.saveUserPreferences({
      ...prefs,
      visitCount: (prefs.visitCount || 0) + 1
    });
  }
  
  // ========== AVATAR PREFERENCES ==========
  
  async setUserAvatar(avatar) {
    const prefs = await this.getUserPreferences();
    return await this.saveUserPreferences({
      ...prefs,
      userAvatar: avatar
    });
  }
  
  async getUserAvatar() {
    const prefs = await this.getUserPreferences();
    return prefs.userAvatar || '👤';
  }
  
  async setDrBenAvatar(avatar) {
    const prefs = await this.getUserPreferences();
    return await this.saveUserPreferences({
      ...prefs,
      drBenAvatar: avatar
    });
  }
  
  async getDrBenAvatar() {
    const prefs = await this.getUserPreferences();
    return prefs.drBenAvatar || '👨‍⚕️';
  }
  
  // ========== MOOD CHECK-INS ==========
  
  /**
   * Save a mood check-in entry
   * @param {object} moodEntry - { id: 'YYYY-MM-DD', mood_value, mood_emoji, mood_label, timestamp, action_type }
   */
  async saveMoodCheckin(moodEntry) {
    const id = moodEntry.id; // Date as ID (YYYY-MM-DD)
    const entryWithMeta = {
      ...moodEntry,
      savedAt: new Date().toISOString()
    };
    await moodCheckins.setItem(id, entryWithMeta);
    return entryWithMeta;
  }
  
  /**
   * Get mood check-in for a specific date
   * @param {string} dateKey - Date in YYYY-MM-DD format
   */
  async getMoodCheckin(dateKey) {
    return await moodCheckins.getItem(dateKey);
  }
  
  /**
   * Get all mood check-ins, sorted by date (newest first)
   */
  async getAllMoodCheckins() {
    const moods = [];
    await moodCheckins.iterate((value) => {
      moods.push(value);
    });
    return moods.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }
  
  /**
   * Get mood check-ins for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   */
  async getMoodCheckinsInRange(startDate, endDate) {
    const allMoods = await this.getAllMoodCheckins();
    return allMoods.filter(mood => {
      const moodDate = mood.id; // Date is stored as ID
      return moodDate >= startDate && moodDate <= endDate;
    });
  }
  
  /**
   * Get mood statistics for a period
   * @param {number} days - Number of days to look back (default: 30)
   */
  async getMoodStats(days = 30) {
    const allMoods = await this.getAllMoodCheckins();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const recentMoods = allMoods.filter(mood => 
      new Date(mood.timestamp) >= cutoffDate
    );
    
    const stats = {
      total: recentMoods.length,
      selected: recentMoods.filter(m => m.action_type === 'selected').length,
      skipped: recentMoods.filter(m => m.action_type === 'skip').length,
      averageMood: 0,
      moodDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      streak: 0
    };
    
    // Calculate average (excluding skips)
    const selectedMoods = recentMoods.filter(m => m.action_type === 'selected');
    if (selectedMoods.length > 0) {
      const sum = selectedMoods.reduce((acc, m) => acc + m.mood_value, 0);
      stats.averageMood = (sum / selectedMoods.length).toFixed(1);
    }
    
    // Calculate distribution
    selectedMoods.forEach(m => {
      if (stats.moodDistribution[m.mood_value] !== undefined) {
        stats.moodDistribution[m.mood_value]++;
      }
    });
    
    // Calculate current streak (consecutive days with any entry)
    let streak = 0;
    const today = new Date();
    for (let i = 0; i < days; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dateKey = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
      
      const hasMood = recentMoods.some(m => m.id === dateKey);
      if (hasMood) {
        streak++;
      } else if (i > 0) {
        // Break streak if missing a day (except today)
        break;
      }
    }
    stats.streak = streak;
    
    return stats;
  }
  
  /**
   * Delete a mood check-in
   * @param {string} dateKey - Date in YYYY-MM-DD format
   */
  async deleteMoodCheckin(dateKey) {
    await moodCheckins.removeItem(dateKey);
  }
  
  /**
   * Clear all mood check-ins
   */
  async clearAllMoodCheckins() {
    await moodCheckins.clear();
  }

  // ========== SELF-HEALING STORAGE (Feb 3, 2026) ==========
  // Apple Design Philosophy: "Proactive, Not Reactive"
  // Automatically detect and repair corrupted IndexedDB before users notice
  
  /**
   * Health check for IndexedDB storage
   * Tests read/write/delete cycle to detect corruption
   * @returns {Object} { healthy: boolean, error?: string, repaired?: boolean }
   */
  async healthCheck() {
    const testKey = `health_check_${Date.now()}`;
    const testValue = { test: true, timestamp: Date.now() };
    
    try {
      // Test write
      await userAuth.setItem(testKey, testValue);
      
      // Test read
      const readValue = await userAuth.getItem(testKey);
      
      // Verify read matches write
      if (!readValue || readValue.test !== true) {
        throw new Error('Read/write mismatch');
      }
      
      // Test delete
      await userAuth.removeItem(testKey);
      
      // Verify delete worked
      const afterDelete = await userAuth.getItem(testKey);
      if (afterDelete !== null) {
        throw new Error('Delete failed');
      }
      
      console.log('[Storage] Health check passed ✓');
      return { healthy: true };
      
    } catch (error) {
      console.warn('[Storage] Health check failed:', error.message);
      return { healthy: false, error: error.message };
    }
  }
  
  /**
   * Auto-repair corrupted storage
   * Clears all stores and reinitializes them
   * @returns {Object} { success: boolean, error?: string }
   */
  async autoRepair() {
    console.warn('[Storage] Starting auto-repair...');
    
    try {
      // Clear all stores
      await healthRecords.clear();
      await conversations.clear();
      await userAuth.clear();
      await userPreferences.clear();
      await moodCheckins.clear();
      
      // Verify stores are accessible after clear
      const verifyCheck = await this.healthCheck();
      
      if (verifyCheck.healthy) {
        console.log('[Storage] Auto-repair successful ✓');
        return { success: true, repaired: true };
      } else {
        throw new Error('Storage still unhealthy after clear');
      }
      
    } catch (error) {
      console.error('[Storage] Auto-repair failed:', error.message);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Initialize storage with health check
   * Called on app startup - automatically repairs if needed
   * @returns {Object} { healthy: boolean, wasRepaired: boolean }
   */
  async initializeWithHealthCheck() {
    console.log('[Storage] Running startup health check...');
    
    const healthResult = await this.healthCheck();
    
    if (healthResult.healthy) {
      return { healthy: true, wasRepaired: false };
    }
    
    // Storage is unhealthy - attempt auto-repair
    console.warn('[Storage] Corruption detected, attempting auto-repair...');
    const repairResult = await this.autoRepair();
    
    if (repairResult.success) {
      console.log('[Storage] Auto-repair successful - storage is now healthy');
      return { healthy: true, wasRepaired: true };
    }
    
    // Auto-repair failed - storage is critically broken
    console.error('[Storage] Auto-repair failed - storage may be unavailable');
    return { healthy: false, wasRepaired: false, error: repairResult.error };
  }
  
  /**
   * Get storage usage statistics
   * Useful for monitoring quota and proactive cleanup
   * @returns {Object} { estimatedUsage: number, stores: Object }
   */
  async getStorageStats() {
    try {
      let totalSize = 0;
      const storeStats = {};
      
      // Count items in each store
      const stores = [
        { name: 'healthRecords', store: healthRecords },
        { name: 'conversations', store: conversations },
        { name: 'userAuth', store: userAuth },
        { name: 'userPreferences', store: userPreferences },
        { name: 'moodCheckins', store: moodCheckins }
      ];
      
      for (const { name, store } of stores) {
        let count = 0;
        let size = 0;
        await store.iterate((value) => {
          count++;
          size += JSON.stringify(value).length;
        });
        storeStats[name] = { count, estimatedBytes: size };
        totalSize += size;
      }
      
      return {
        totalEstimatedBytes: totalSize,
        totalEstimatedKB: Math.round(totalSize / 1024),
        stores: storeStats
      };
      
    } catch (error) {
      console.error('[Storage] Failed to get stats:', error);
      return { error: error.message };
    }
  }
}

export default new HealthStorageService();
